(() => {
  if (customElements.get('studio-navigation')) return;
  class StudioNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller || !window.HTMLDialogElement) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.panel = this.querySelector('.sn-panel');
      this.disclosure = this.querySelector('.sn-browse');
      const openedBeforeReady = this.disclosure.open;
      this.disclosure.open = false;
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'sn-dialog';
      this.dialog.id = `StudioMenuDialog-${this.dataset.sectionId}`;
      this.dialog.setAttribute('aria-labelledby', `StudioMenuLabel-${this.dataset.sectionId}`);
      this.dialog.append(this.panel);
      this.append(this.dialog);
      this.classList.add('sn-enhanced');
      this.openers = [...this.querySelectorAll('[data-sn-open]')];
      this.openers.forEach(opener => {
        opener.hidden = false;
        opener.setAttribute('aria-haspopup', 'dialog');
        opener.setAttribute('aria-controls', this.dialog.id);
        opener.setAttribute('aria-expanded', 'false');
        opener.addEventListener('click', event => { event.preventDefault(); this.open(opener); }, {signal});
      });
      this.querySelector('.sn-filter').hidden = false;
      this.querySelector('[data-sn-close]').hidden = false;
      this.querySelector('[data-sn-back]').hidden = false;
      this.panels = [...this.querySelectorAll('[data-sn-content]')];
      this.triggers = [...this.querySelectorAll('[data-sn-category]')];
      this.filter = this.querySelector('[data-sn-filter]');
      this.results = this.querySelector('[data-sn-results]');
      this.resultList = this.querySelector('[data-sn-result-list]');
      this.entries = [];
      const seen = new Set();
      this.panels.forEach(panel => panel.querySelectorAll('a[data-sn-index],.sn-section-heading>a').forEach(link => {
        const href = link.getAttribute('href');
        if (seen.has(href)) return;
        seen.add(href);
        const label = link.matches('.sn-section-heading>a') ? panel.querySelector('h3').textContent.trim() : link.textContent.trim();
        this.entries.push({href, label});
      }));
      this.entries.sort((a,b) => a.label.localeCompare(b.label));
      this.setupArtwork(signal);
      this.select('overview');
      this.addEventListener('click', event => {
        const selector = event.target.closest('[data-sn-category],[data-sn-select]');
        if (selector) {
          event.preventDefault();
          this.filter.value = '';
          this.select(selector.dataset.snCategory || selector.dataset.snSelect, true);
        }
        if (event.target.closest('[data-sn-close]')) this.close();
        if (event.target.closest('[data-sn-back]')) {
          this.filter.value = '';
          this.select('overview');
          this.triggers[1]?.focus();
        }
      }, {signal});
      this.filter.addEventListener('input', () => this.search(), {signal});
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.close();
      }, {signal});
      this.dialog.addEventListener('close', () => this.restore(), {signal});
      this.dialog.addEventListener('keydown', event => {if(event.key === 'Escape'){event.preventDefault();event.stopPropagation();this.close();}}, {capture:true,signal});
      this.dialog.addEventListener('cancel', event => {event.preventDefault();this.close();}, {signal});
      window.addEventListener('resize', () => {if(this.dialog.open) this.position();}, {signal});
      if(openedBeforeReady) this.open(this.disclosure.querySelector('summary'));
    }
    position() {
      const bottom = this.querySelector('header').getBoundingClientRect().bottom;
      this.dialog.style.setProperty('--sn-top', `${Math.max(12, Math.min(bottom + 8, window.innerHeight - 340))}px`);
    }
    open(opener) {
      if (this.dialog.open) return;
      this.returnFocus = opener;
      this.position();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal();
      this.updateArtwork(this.panels.find(panel => !panel.hidden));
      this.openers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.querySelector('[data-sn-close]').focus({preventScroll:true});
    }
    close() {if(this.dialog.open) this.dialog.close();}
    restore() {
      this.clearArtwork();
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.openers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (this.returnFocus?.isConnected) this.returnFocus.focus({preventScroll:true});
    }
    select(key, focusContent = false) {
      const selected = this.panels.find(panel => panel.dataset.snContent === key) || this.panels[0];
      key = selected.dataset.snContent;
      this.panels.forEach(panel => {panel.hidden = panel !== selected;});
      this.triggers.forEach(trigger => {
        const active = trigger.dataset.snCategory === key;
        if(active) trigger.setAttribute('aria-current','true'); else trigger.removeAttribute('aria-current');
      });
      this.results.hidden = true;
      this.dialog.classList.remove('sn-filtering');
      this.dialog.classList.toggle('sn-show-category', key !== 'overview');
      this.updateArtwork(selected);
      this.querySelector('.sn-contents').scrollTop = 0;
      this.querySelector('.sn-stage').scrollTop = 0;
      if(focusContent) {
        const heading = selected.querySelector('h3');
        heading.setAttribute('tabindex','-1');
        heading.focus({preventScroll:true});
      }
    }
    search() {
      const words = this.filter.value.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
      if(!words.length) {this.select('overview');return;}
      this.panels.forEach(panel => {panel.hidden = true;});
      this.triggers.forEach(trigger => trigger.removeAttribute('aria-current'));
      this.results.hidden = false;
      this.dialog.classList.add('sn-filtering');
      this.dialog.classList.remove('sn-show-category');
      this.clearArtwork();
      this.resultList.replaceChildren();
      const matches = this.entries.filter(entry => words.every(word => entry.label.toLocaleLowerCase().includes(word)));
      matches.forEach(entry => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = entry.href;
        link.textContent = entry.label;
        item.append(link);
        this.resultList.append(item);
      });
      const count = this.querySelector('[data-sn-count]');
      count.textContent = `${count.dataset.label}: ${matches.length}`;
      this.querySelector('[data-sn-empty]').hidden = matches.length > 0;
      this.querySelector('.sn-contents').scrollTop = 0;
      this.querySelector('.sn-stage').scrollTop = 0;
    }
    setupArtwork(signal) {
      this.artRevision = 0;
      this.artImages = new Map();
      this.artMedia = matchMedia('(min-width: 901px)');
      this.artReduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.artBackdrop = document.createElement('div');
      this.artBackdrop.className = 'sn-art-backdrop';
      this.artBackdrop.setAttribute('aria-hidden', 'true');
      this.artBackdrop.dataset.visible = 'false';
      this.artCanvas = document.createElement('div');
      this.artCanvas.className = 'sn-art-canvas';
      this.artBackdrop.append(this.artCanvas);
      this.querySelector('.sn-stage').prepend(this.artBackdrop);
      this.classList.add('sn-art-mode');
      this.artMedia.addEventListener('change', () => this.updateArtwork(this.panels.find(panel => !panel.hidden)), {signal});
      this.artReduced.addEventListener('change', () => {
        this.stopArtworkAnimations();
        if (this.artCanvas.lastElementChild) {
          const last = this.artCanvas.lastElementChild;
          last.style.opacity = '1';
          this.artCanvas.replaceChildren(last);
        }
      }, {signal});
    }
    clearArtwork() {
      this.artRevision++;
      this.artBackdrop.dataset.visible = 'false';
      delete this.artBackdrop.dataset.loading;
      this.artTransition?.skipTransition();
      this.stopArtworkAnimations();
      this.artCanvas.replaceChildren();
      delete this.artBackdrop.dataset.category;
    }
    prepareArtwork(key, card) {
      if (this.artImages.has(key)) return this.artImages.get(key);
      const source = card.dataset.snArtOriginal || card.dataset.snArtFallback;
      const fallback = card.dataset.snArtFallback;
      const pending = new Promise(resolve => {
        const picture = new Image();
        picture.alt = '';
        picture.className = 'sn-art-layer';
        picture.decoding = 'async';
        let usedFallback = !card.dataset.snArtOriginal;
        picture.onload = async () => {
          try { await picture.decode(); } catch {}
          picture.onload = null;
          picture.onerror = null;
          resolve(picture.naturalWidth ? picture : null);
        };
        picture.onerror = () => {
          if (!usedFallback && fallback) {
            usedFallback = true;
            picture.src = fallback;
          } else {
            picture.onload = null;
            picture.onerror = null;
            this.artImages.delete(key);
            resolve(null);
          }
        };
        picture.src = source;
      });
      this.artImages.set(key, pending);
      return pending;
    }
    async updateArtwork(panel) {
      const key = panel?.dataset.snContent;
      const card = panel?.querySelector('.sn-category-art [data-sn-art-fallback]');
      if (!this.dialog.open || !this.artMedia.matches || !key || key === 'overview' || !card) {
        this.clearArtwork();
        return;
      }
      this.artBackdrop.dataset.visible = 'true';
      if (this.artBackdrop.dataset.category === key && this.artCanvas.childElementCount && !this.artBackdrop.dataset.loading) {
        return;
      }
      const revision = ++this.artRevision;
      this.artBackdrop.dataset.loading = 'true';
      const loaded = await this.prepareArtwork(key, card);
      if (revision !== this.artRevision || !this.isConnected || !this.dialog.open) return;
      delete this.artBackdrop.dataset.loading;
      if (!loaded) {
        this.clearArtwork();
        return;
      }
      const picture = loaded.cloneNode();
      picture.dataset.category = key;
      this.artBackdrop.dataset.category = key;
      this.showArtwork(picture, revision);
    }
    stopArtworkAnimations() {
      this.artTransition?.skipTransition();
      this.artTransition = null;
      if (!this.artCanvas) return;
      [...this.artCanvas.children].forEach(layer => {
        const opacity = getComputedStyle(layer).opacity;
        layer.getAnimations().forEach(animation => animation.cancel());
        layer.style.opacity = opacity;
      });
    }
    showArtwork(picture, revision) {
      this.stopArtworkAnimations();
      const duration = parseFloat(getComputedStyle(this).getPropertyValue('--sn-art-duration')) || 450;
      if (this.artReduced.matches || !picture.animate) {
        picture.style.opacity = '1';
        this.artCanvas.replaceChildren(picture);
        return;
      }
      if (typeof this.artCanvas.startViewTransition === 'function') {
        const transition = this.artCanvas.startViewTransition(() => {
          if (revision !== this.artRevision) return;
          picture.style.opacity = '1';
          this.artCanvas.replaceChildren(picture);
        });
        this.artTransition = transition;
        transition.ready.catch(() => {});
        transition.finished.catch(() => {});
        return;
      }
      const outgoing = [...this.artCanvas.children];
      picture.style.opacity = '1';
      this.artCanvas.append(picture);
      const options = {duration, easing:'cubic-bezier(.22,.61,.36,1)', fill:'both'};
      outgoing.forEach(layer => {
        layer.animate([{opacity:parseFloat(layer.style.opacity || '1')},{opacity:0}], options).finished.catch(() => {});
      });
      const incoming = picture.animate([{opacity:0},{opacity:1}], options);
      incoming.finished.then(() => {
        if (revision !== this.artRevision) return;
        this.artCanvas.replaceChildren(picture);
        incoming.cancel();
        picture.style.opacity = '1';
      }).catch(() => {});
    }
    disconnectedCallback() {
      if(this.dialog?.open) {this.dialog.close();this.restore();}
      if (this.artBackdrop) {
        this.clearArtwork();
        this.artBackdrop.remove();
      }
      this.controller?.abort();
      this.controller = null;
      if(this.panel && this.disclosure) this.disclosure.append(this.panel);
      this.dialog?.remove();
      this.classList.remove('sn-enhanced');
      this.classList.remove('sn-art-mode');
    }
  }
  customElements.define('studio-navigation', StudioNavigation);
})();
